import type { StoreDefinition } from 'pinia'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'

import type { BulkAction } from '@/javascript/components/bulk_actions_dialog'
import type {
	Agency,
	AgencyBackOffice,
	AgencyInformation,
	CAMember,
	CurrentUser,
	ErrorType,
	Language,
} from '@/javascript/interfaces'
import { standardDateFormat } from '@/javascript/lib/validate_date/process-date-string'
import type { CABreadcrumbsLink, CACommandResult } from '@ds/interfaces'
import { COUNTDOWN, DELAY } from '@vuejs/helpers/_constants'

import type { MemberAction } from '../services/administratorDashboard/interfaces'
import api from '../services/api'
import type { Member } from '../services/members/interfaces'
import { getStates } from '../services/states'
import { getAgencyTeam } from '../services/team'
import type { State } from '../services/trainingCurriculums/interfaces'
import { getTrialDetails } from '../services/trialSeatUpgrade'
import { getAgencyId } from '../utils/getAgencyId'
import { getFranchisorId } from '../utils/getFranchisorId'
import { getIdFromUrl } from '../utils/getIdFromUrl'
import { useAdministratorStore } from './AdministratorStore'
import { useCurriculumsStore } from './CurriculumsStore'
import { useFeatureFlagStore } from './FeatureFlagStore'
import { useFranchisorStore } from './FranchisorStore'
import { useMemberTrainingStore } from './MemberTrainingStore'
import { useNotificationStore } from './NotificationStore'
import { useTeamStore } from './TeamStore'

export type PopoverOptions = '' | 'Edit name' | 'Manage due date'

export const useGlobalStore: StoreDefinition<string, any, any, any> = defineStore('global', () => {
	// State
	const administratorStore = useAdministratorStore()
	const agencies = ref<CACommandResult[]>([])
	const agency = ref<Agency | null | undefined>(null)
	const agencyBackOffice = ref<AgencyBackOffice>({
		backOfficeIntegrationEnabled: false,
		backOfficeIntegrationProviderName: '',
		marketingProductName: '',
	})
	const agencyCreatedAt = ref('')
	const agencyInfo = ref<AgencyInformation>({} as AgencyInformation)
	const agencyMembers = ref<Member[]>([])
	const bulkAction = ref<BulkAction>({
		action: '' as MemberAction,
		actionDescriptionText: '',
		actionText: '',
		additionalMembersText: <></>,
		bodyText: '',
		memberIds: [] as number[],
		membersText: '',
	})
	const currentUser = ref<CurrentUser | null | undefined>(null)
	const curriculumsStore = useCurriculumsStore()
	const emittedEvent = ref('')
	const franchisorSSOPath = ref('')
	const isAdvanceReporting = ref(false)
	const isSharedCompletionsEnabled = ref(false)
	const loadingAgencyInfo = ref(false)
	const memberTrainingStore = useMemberTrainingStore()
	const notificationStore = useNotificationStore()
	const page = ref('')
	const previousRoute = ref<RouteLocationNormalized | null>(null)
	const previousRouteUrl = ref('')
	const selectAllChecked = ref(false)
	const selectAllCheckedOnPage = ref<{ [page: number]: boolean }>({})
	const selectedCATableMembers = ref<CAMember[]>([])
	const selectedLanguage = ref<Language>('' as Language)
	const showPopover = ref<{ id: number | null; name: PopoverOptions }>({
		id: null,
		name: '',
	})
	const showTrainingDashboard = ref(true)
	const simpleHeaderLayout = ref(false)
	const states = ref<State[]>([])
	const twilioSuccessCode = ref('')
	const twilioUserErrorMessages = ref<{ [key: string]: string }>({})
	const userId = ref('')

	// Getters
	const agencyId = computed((): number => getAgencyId())

	const someSelectAllCheckboxIsTrue = computed((): boolean =>
		Object.values(selectAllCheckedOnPage.value).some(value => value === true)
	)

	const selectedCheckboxNotOnPage = computed(
		(): boolean => !selectAllCheckedOnPage.value[useTeamStore().pagination.currentPage]
	)

	const checkedOnAnotherPage = computed(
		(): boolean => someSelectAllCheckboxIsTrue.value && selectedCheckboxNotOnPage.value
	)

	const currentDate = computed((): string => {
		const dateFormatOptions: Intl.DateTimeFormatOptions = {
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			month: 'long',
			second: '2-digit',
			timeZoneName: 'longOffset',
			weekday: 'short',
			year: 'numeric',
		}

		return new Date().toLocaleString('en-US', dateFormatOptions)
	})

	const currentDateString = computed((): string => standardDateFormat(new Date()))

	const currentUrl = computed((): string => window.location.href)

	const eligibleAgenciesUrl = computed((): string => '/api/agencies/eligible_agencies')

	const franchisorId = computed((): number | null => currentUser.value?.franchisor?.id || getFranchisorId())

	const hasFranchisorId = computed((): boolean => !!getFranchisorId())

	const franchisorLink = computed((): CABreadcrumbsLink[] => {
		const computedFranchisorId = currentUser.value?.franchisor?.id || getFranchisorId()

		return [
			{
				active: pageIsFranchisor.value,
				dataTest: 'navigation-breadcrumbs-franchisor-link',
				name: computedFranchisorId ? 'Multi-location Dashboard' : 'Dashboard',
				to: computedFranchisorId ? `/agencies?franchisor_id=${computedFranchisorId}` : '/',
			},
		]
	})

	const hasAgency = computed((): boolean => !!(agency.value?.id || currentUser.value?.agency?.id))

	const inFreeTrial = computed(
		(): boolean =>
			agency.value?.status === 'free_trial' ||
			currentUser.value?.agency?.status === 'free_trial' ||
			agencyInfo.value?.is_in_free_trial ||
			false
	)

	const isAdvanceReportingEnabled = computed((): boolean => isAdvanceReporting.value)

	const isBillingAdmin = computed(
		(): boolean =>
			currentUser.value?.isSuper ||
			currentUser.value?.isFranchisorManager ||
			currentUser.value?.isAgencyBillingManager ||
			currentUser.value?.isAgencyOwner ||
			false
	)

	const isCurriculumDiscoverabilityEnabled = computed((): boolean =>
		useFeatureFlagStore().isFeatureEnabled('curriculum_discoverability')
	)

	const isFranchisor = computed((): boolean => currentUser.value?.isFranchisorManager || false)

	const isFromTeamPage = computed(
		(): boolean => previousRouteUrl.value.includes('team') || currentUrl.value.includes('team')
	)

	const isMemberOnly = computed(
		(): boolean =>
			!currentUser.value?.isSuper &&
			!currentUser.value?.isAgencyBillingManager &&
			!currentUser.value?.isAgencyManager &&
			!currentUser.value?.isFranchisorManager &&
			!currentUser.value?.isMoreThanCaregiver &&
			(currentUser.value?.isCaregiver || false)
	)

	const isMobileView = computed((): boolean => {
		const element = document.querySelector('body')

		return element ? element.classList.contains('mobile-view') : false
	})

	const isMultiLocation = computed((): boolean => page.value === 'franchisor')

	const pageIsDashboard = computed((): boolean => currentUrl.value.includes('compliance_report'))

	const pageIsFranchisor = computed((): boolean => {
		const franchisorStore = useFranchisorStore()

		return (
			currentUrl.value === `/agencies?franchisor_id=${franchisorStore.franchisorId}` ||
			hasFranchisorId.value &&
				agencies.value.length >= 1 &&
				currentUrl.value === '/agencies' ||
			hasFranchisorId.value &&
				agencies.value.length >= 1 &&
				currentUrl.value === `/agencies?franchisor_id=${franchisorStore.franchisorId}`
		)
	})

	const showAddAllToOrganizations = computed((): boolean => {
		if (!currentUser.value) {
			return false
		}

		return (
			(currentUser.value.isFranchisorManager || currentUser.value.isSuper) &&
			!!currentUser.value.franchisor
		)
	})

	const showFranchise = computed((): boolean => {
		if (!currentUser.value) {
			return false
		}

		return currentUser.value.isFranchisorManager || currentUser.value.isSuper
	})

	const teamBreadcrumbLink = computed((): CABreadcrumbsLink[] => {
		const goBackToPage = window.sessionStorage.getItem('goBackToPage')

		const showInsightsLink =
			!goBackToPage?.includes('team') &&
			!!window.sessionStorage.getItem('selectedGroupId') &&
			page.value !== 'team' &&
			!previousRouteUrl.value.includes('team')

		const hideTeamAndInsightsLinks = page.value === 'catalog'

		switch (true) {
			case hideTeamAndInsightsLinks:
				return []

			case page.value === 'designSystem':
				return [
					{
						active: false,
						dataTest: 'navigation-breadcrumbs-design-system',
						name: 'Design System',
						to: '/design-system',
					},
				]

			case showInsightsLink:
				return [
					{
						active: false,
						dataTest: 'navigation-breadcrumbs-insights',
						name: 'Insights',
						to: `/agencies/${getAgencyId()}/compliance_report`,
					},
				]

			default:
				return [
					{
						active: false,
						dataTest: 'navigation-breadcrumbs-team',
						name: 'Team',
						to: `/agencies/${getAgencyId()}/team`,
					},
				]
		}
	})

	// Actions
	const checkAndDisplaySessionStorageMessage = (): void => {
		const successMessage = window.sessionStorage.getItem('successMessage')
		const sharedCompletionsFound = window.sessionStorage.getItem('sharedCompletionsFound')
		const errorMessage = window.sessionStorage.getItem('errorMessage')

		if (successMessage) {
			notificationStore.onHandleSuccessMessages({ message: successMessage })
			window.sessionStorage.removeItem('successMessage')
		}

		if (errorMessage) {
			notificationStore.onHandleErrorMessages({ error: { message: errorMessage } })
			window.sessionStorage.removeItem('errorMessage')
		}

		if (sharedCompletionsFound) {
			notificationStore.onHandleSuccessMessages({ message: sharedCompletionsFound, type: 'info' })
			window.sessionStorage.removeItem('sharedCompletionsFound')
		}
	}

	const clearSelectedValues = (): void => {
		selectedCATableMembers.value = []
		selectAllCheckedOnPage.value = {}
		selectAllChecked.value = false
	}

	const closeModal = (): void => {
		emittedEvent.value = ''
		selectedCATableMembers.value = []

		administratorStore.selectedMembers = []
		memberTrainingStore.resetData()
		curriculumsStore.resetData()
	}

	const closePopover = (): void => {
		showPopover.value = { id: null, name: '' }
	}

	const countDownTimer = ({
		newMessage,
		response,
	}: {
		newMessage?: string
		response?: { data?: { success_messages?: string } }
	}): void => {
		let countdown = COUNTDOWN
		const interval = setInterval(() => {
			countdown--
			if (newMessage) {
				notificationStore.onHandleSuccessMessages({ message: `${newMessage} Refreshing page in ${countdown} seconds.` })
			}

			if (response && response.data?.success_messages) {
				notificationStore
					.onHandleSuccessMessages({ message: `${response.data.success_messages} Refreshing page in ${countdown} seconds.` })
			}

			if (countdown === 0) {
				clearInterval(interval)
				window.location.reload()
			}
		}, DELAY.ONE_SECOND)
	}

	const fillAgencyBackOffice = async (): Promise<void> => {
		try {
			const response = await getAgencyTeam(api, getAgencyId())

			agencyBackOffice.value = {
				backOfficeIntegrationEnabled: response.agency?.back_office_integration_enabled,
				backOfficeIntegrationProviderName: response.agency?.back_office_integration_provider_name,
				marketingProductName: response.agency?.marketing_product_name,
			}
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		}
	}

	const formatSeatsUsed = (seatUsed = 0): number => {
		return Math.trunc(seatUsed) ?? 0
	}

	const getAgencyInfo = async (): Promise<void> => {
		loadingAgencyInfo.value = true
		try {
			const id = getIdFromUrl()

			if (id !== 0) {
				const response = await getTrialDetails(api, `${id}`)
				const { data } = response

				if (data.agency) {
					agencyInfo.value = {
						...agencyInfo.value,
						days_left: data.agency.days_left,
						is_expired: data.agency.is_expired,
						total_trial_days: data.agency.total_trial_days,
					}
				}
			}
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		} finally {
			loadingAgencyInfo.value = false
		}
	}

	const getTwilioUserErrorMessage = (errorCode: string): string => {
		return twilioUserErrorMessages.value[errorCode]
	}

	const hasTwilioUserError = (errorCode: string): boolean => {
		return Object.prototype.hasOwnProperty.call(twilioUserErrorMessages.value, errorCode)
	}

	const identifyPage = (url: string): string => {
		const teamPagePattern = /^\/agencies\/\d+\/team\/?$/

		const groupMembersPagePattern = /^\/agencies\/\d+\/team\/\d+\/members\/?(\d+\/?)?$/

		if (teamPagePattern.test(url)) {
			return 'teamPage'
		} else if (groupMembersPagePattern.test(url)) {
			return 'groupMembersPage'
		} else {
			return 'unknownPage'
		}
	}

	const loadStates = async (): Promise<void> => {
		if (states.value.length > 0) return

		try {
			const response = await getStates(api)

			states.value = response.data.states.map(([
				name,
				abbreviation,
				timezone,
			]) => ({
				abbreviation,
				name,
				timezone,
			}))
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		}
	}

	const openSendBulkMessage = (): void => {
		emittedEvent.value = 'send-bulk-message'
	}

	const openSendGroupMessage = (): void => {
		emittedEvent.value = 'send-group-message'
	}

	const openSendMemberMessage = (): void => {
		administratorStore.selectedMembers = []
		emittedEvent.value = 'send-member-message'
	}

	const resetCAModal = (): void => {
		emittedEvent.value = ''
	}

	const scrollToTop = (scrollContainerId?: string): void => {
		const scrollContainer = scrollContainerId
			? document.getElementById(scrollContainerId)
			: undefined

		if (scrollContainer) {
			if (scrollContainer.scrollHeight > scrollContainer.clientHeight) {
				scrollContainer.scrollTo({ behavior: 'smooth', top: 0 })
			}
		} else {
			window.scrollTo({ behavior: 'smooth', top: 0 })
		}
	}

	return {
		// State
		agencies,
		agency,
		agencyBackOffice,
		agencyCreatedAt,
		agencyInfo,
		agencyMembers,
		bulkAction,
		currentUser,
		emittedEvent,
		franchisorSSOPath,
		isAdvanceReporting,
		isSharedCompletionsEnabled,
		loadingAgencyInfo,
		page,
		previousRoute,
		previousRouteUrl,
		selectAllChecked,
		selectAllCheckedOnPage,
		selectedCATableMembers,
		selectedLanguage,
		showPopover,
		showTrainingDashboard,
		simpleHeaderLayout,
		states,
		twilioSuccessCode,
		twilioUserErrorMessages,
		userId,

		// Getters
		agencyId,
		checkedOnAnotherPage,
		currentDate,
		currentDateString,
		currentUrl,
		eligibleAgenciesUrl,
		franchisorId,
		franchisorLink,
		hasAgency,
		hasFranchisorId,
		inFreeTrial,
		isAdvanceReportingEnabled,
		isBillingAdmin,
		isCurriculumDiscoverabilityEnabled,
		isFranchisor,
		isFromTeamPage,
		isMemberOnly,
		isMobileView,
		isMultiLocation,
		pageIsDashboard,
		pageIsFranchisor,
		selectedCheckboxNotOnPage,
		showAddAllToOrganizations,
		showFranchise,
		someSelectAllCheckboxIsTrue,
		teamBreadcrumbLink,

		// Actions
		checkAndDisplaySessionStorageMessage,
		clearSelectedValues,
		closeModal,
		closePopover,
		countDownTimer,
		fillAgencyBackOffice,
		formatSeatsUsed,
		getAgencyInfo,
		getTwilioUserErrorMessage,
		hasTwilioUserError,
		identifyPage,
		loadStates,
		openSendBulkMessage,
		openSendGroupMessage,
		openSendMemberMessage,
		resetCAModal,
		scrollToTop,
	}
})
